import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import cx from 'classnames';

import { Urls, AppConfig } from 'constants/constants';
import { didPropChange } from 'utils/props';
import { yup } from 'utils/forms';

import Link, { selectLink } from 'store/models/Link';
import Campaign from 'store/models/Campaign';
import RouteState, { selectQuery } from 'store/RouteState';
import { selectConfigItem } from 'store/AppState';

import { ContentCenter, Header, Title, Breadcrumbs } from 'components/Content';
import Form, { withForm, } from 'components/forms/Form';
import { Card, CardHead, CardTitle, CardItem, CardInputItem, ItemLabel, ItemInput } from 'components/Card';
import { ErrorList } from 'components/forms/Error';
import Label from 'components/forms/Label';
import { SaveButton, CancelButton, XSmallButton } from 'components/Button';
import { TextInput, TextareaInput } from 'components/forms/TextInput';
import ActionBar from 'components/ActionBar';






// ======================
// HELPERS
// ====================== 

const LINK_TOKEN = '{LINK}';
const FAKE_LINK = 'https://hppy.tv/91yZuHSk8'

export const formatMessage = (message, url, chatbot) => {
  let component;
  if(message && message.length) {
    let t = _.split(message, LINK_TOKEN);
    component = (<>
      {t[0]}
      {message.indexOf(LINK_TOKEN) >= 0 ? <a href={url}>{FAKE_LINK}</a> : null }
      {t[1]}
    </>);
  } 
  else if(url && url.length) {
    component = <a href={url}>{FAKE_LINK}</a>;
  }
  else {
    component = "---";
  }
  return (
    <div className="preview-message">
      <strong><img src="/assets/images/ui/twitch_mod_badge.png" />{chatbot}:</strong>
      {component}
    </div>
  )
}

const SCHEMA = {
  name: {
    validate: yup.string().required(),
  },
  url: {
    validate: yup.string().url().required(),
  },
  message: {},
}


// ======================
// STORE
// ====================== 
const actions = {
  fetchData: linkId => dispatch => {
    return dispatch(Link.actions.fetchLink(linkId));
  },
  createLink: Link.actions.createLink,
  updateLink: Link.actions.updateLink,
  navigate: RouteState.actions.navigate,
  goBack: RouteState.actions.back,
  addToCampaign: Campaign.actions.addLink,
};

const selectors = (state, props) => createSelector(
  (state, props) => selectLink(state, props.match.params.linkId),
  (state, props) => props.match.params.linkId,
  state => selectQuery(state, 'back') === 'true',
  (state) => selectQuery(state, 'campaign'),
  state => selectConfigItem(state, AppConfig.CHATBOT),
  (link, linkId, back, campaignId, chatbot) => ({ 
    link, 
    linkId, 
    back,
    campaignId,
    chatbot
  }),
)

const withConnect = connect(selectors, actions);



// ======================
// COMPONENT
// ====================== 
class LinkEditScreen extends React.Component {
  _messageRef = null
  _onMessageRef = n => this._messageRef = n

  state = {
    loading: false,
    saving: false,
  }
  
  componentDidMount = async () => {
    let { link, linkId, setSchema, setDefaults, fetchData } = this.props;
    setSchema(SCHEMA);

    this.setState({ loading: true });
    if(link) {
      link
        |> _.pick(#, _.keys(SCHEMA))
        |> setDefaults;
    } else if(linkId) {
      await fetchData(linkId);
    }
    this.setState({ loading: false });
  }

  componentDidUpdate(prev) {
    if(didPropChange(this.props, prev, 'link')) {
      let { link, setDefaults } = this.props;
      link 
        |> _.pick(#, _.keys(SCHEMA))
        |> setDefaults;
    }
  }

  onSave = async () => {
    console.log("SUBMIT")
    let isValid = await this.props.validateForm();
    if(!isValid)
      return;

    try { 
      this.setState({ saving: true });
      let linkId;
      if(this.props.linkId) {
        await this.props.updateLink(this.props.linkId, this.props.editedValues());
        linkId = this.props.linkId;
      } else {
        let results = await this.props.createLink(this.props.editedValues());
        linkId = results?.body?._id;
      }
      if(this.props.campaignId && linkId)
        this.props.addToCampaign(this.props.campaignId, linkId);
      this.onNavigateBack();
    } catch(err) {
      this.props.setErrors({ server: err.message });
    }
  }
  onCancel = () => this.onNavigateBack();
  onNavigateBack = () => this.props.back 
    ? this.props.goBack()
    : this.props.navigate(Urls.CAMPAIGNS)
  onInsertLink = () => {
    this.props.setValues('message', this.props.values.message |> _.trim |> `${#} ${LINK_TOKEN} `);
    this._messageRef?.getInput()?.focus();
  }

  render() {
    let { link, chatbot, values, errors, onInputChange } = this.props;
    let { loading, saving  } = this.state;
    let isCreate = !loading && _.isEmpty(link);
    let title = isCreate
      ? "Create New Link"
      : `Edit Link: ${link?.name}`;
    let preview = formatMessage(values.message ?? '', values.url, chatbot);
    
    return (
      <ContentCenter className="link-edit">
        <Header>
          <Title>{title}</Title>
        </Header>
        <ErrorList errors={errors} />
        <Form onSubmit={this.onSave}>
          <Card>
            <CardInputItem>
              <ItemLabel>
                <Label>Name</Label>
              </ItemLabel>
              <ItemInput>
                <TextInput
                  name="name"
                  helper="A name to recognize this in your reports"
                  value={values['name']}
                  onChange={onInputChange} />
              </ItemInput>
            </CardInputItem>
            <CardInputItem>
              <ItemLabel>
                <Label>Url</Label>
              </ItemLabel>
              <ItemInput>
                <TextInput
                  name="url"
                  helper="The url the viewer will be sent to"
                  value={values['url']}
                  onChange={onInputChange} />
              </ItemInput>
            </CardInputItem>
            <CardInputItem>
              <ItemLabel>
                <Label>Chat Message</Label>
              </ItemLabel>
              <ItemInput>
                <TextareaInput
                  ref={this._onMessageRef}
                  name="message"
                  placeholder="Enter {LINK} where the link should be injected"
                  helper="The full message posted to chat, with your link. Use {LINK} in place of where your link should be."
                  value={values['message']}
                  onChange={onInputChange} />
                <XSmallButton className="insert" onClick={this.onInsertLink}>Insert Link</XSmallButton>
              </ItemInput>
            </CardInputItem>
            <CardInputItem>
              <ItemLabel>
                <Label>Chat Preview</Label>
              </ItemLabel>
              <ItemInput>{preview}</ItemInput>
            </CardInputItem>
          </Card>
          <ActionBar>
            <SaveButton loading={saving} submit={true} />
            <CancelButton onClick={this.onCancel} />
          </ActionBar>
        </Form>
      </ContentCenter>)
  }
}
export default LinkEditScreen
  |> withConnect
  |> withForm;
